import type { TCreateSlice } from "@/store";

import type { GridState } from "@/ui/molecules/Grid";

type TStateGridState = {
    savedGridState: TGridState | null;
    gridState: TGridState;
    saveGridStatus: "idle" | "resolved" | "rejected";
};

export type TGridState = Pick<
    GridState,
    | "columnSizing"
    | "filter"
    | "sort"
    | "columnOrder"
    | "columnVisibility"
    | "partialColumnState"
>; // @FIXME: prevent from importing component parts into store

type TActionsPublic = {
    initialiseSavedGridView: () => void;
    saveGridState: (gridState: TGridState) => void;
    resetViewButtonClicked: () => void;
};

export type TSliceGridState = TStateGridState & TActionsPublic;

type TSliceDependencies = Record<string, never>;

const GRID_STATE_STORAGE_KEY = "gridState";

export const initialStateGridState = {
    savedGridState: null,
    gridState: {},
    saveGridStatus: "idle",
} as const satisfies TStateGridState;

export const createSliceGridState: TCreateSlice<
    TSliceGridState,
    TSliceDependencies
> = () => set => {
    const actionsPublic: TActionsPublic = {
        initialiseSavedGridView: () => {
            const storedGridState = localStorage.getItem(
                GRID_STATE_STORAGE_KEY,
            );

            try {
                if (storedGridState !== null) {
                    const gridState = JSON.parse(storedGridState) as TGridState;
                    set(
                        {
                            savedGridState: gridState,
                        },
                        false,
                        "gridState/initialiseSavedGridView/columnState",
                    );
                }
            } catch (error) {
                console.error(
                    `initialiseSavedGridView: Error initialising saved grid view: ${(error as Error).message}`,
                );
            }
        },
        saveGridState: gridState => {
            try {
                localStorage.setItem(
                    GRID_STATE_STORAGE_KEY,
                    JSON.stringify(gridState),
                );
                set(
                    {
                        savedGridState: gridState,
                        saveGridStatus: "resolved",
                    },
                    false,
                    "gridState/saveGridState/resolved",
                );
                setTimeout(() => {
                    set(
                        {
                            saveGridStatus: "idle",
                        },
                        false,
                        "gridState/saveGridState/idle",
                    );
                }, 2000);
            } catch {
                set(
                    {
                        saveGridStatus: "rejected",
                    },
                    false,
                    "gridState/saveGridState/rejected",
                );
            }
        },

        resetViewButtonClicked: () => {
            localStorage.removeItem(GRID_STATE_STORAGE_KEY);
            set(
                {
                    savedGridState: null,
                    saveGridStatus: "resolved",
                },
                false,
                "gridState/resetViewButtonClicked/resolved",
            );
            setTimeout(() => {
                set(
                    {
                        saveGridStatus: "idle",
                    },
                    false,
                    "gridState/resetSaveGridStatus/idle",
                );
            }, 2000);
        },
    };

    return {
        ...initialStateGridState,
        ...actionsPublic,
    };
};
